import type { RealtimeFanout, RealtimeNotifyEvent } from "@gencow/core";
import {
  subscriptionKeyMatchesQueryKey,
  verifyRealtimeChannelToken,
  verifyRealtimeQuerySubscriptionToken,
} from "@gencow/core";
import type { WSContext } from "hono/ws";

export interface LocalRealtimeRegistry extends RealtimeFanout {
  subscribe(queryKey: string, ws: WSContext): void;
  unsubscribeAll(ws: WSContext): void;
  registerClient(ws: WSContext): void;
  deregisterClient(ws: WSContext): void;
  handleWsMessage(ws: WSContext, raw: string | ArrayBuffer): void;
}

export function createLocalRealtimeFanout(options?: {
  appName?: string;
  channelTokenSecret?: string;
  nowMs?: () => number;
}): LocalRealtimeRegistry {
  const subscribers = new Map<string, Set<WSContext>>();
  const authorizedQuerySubscribers = new Map<
    string,
    Map<
      WSContext,
      { query: string; subscriptionKey: string; scopeHash: string; expiresAt: number; epoch?: string }
    >
  >();
  const channelSubscribers = new Map<
    string,
    Map<WSContext, { channel: string; expiresAt: number; epoch?: string }>
  >();
  const connectedClients = new Set<WSContext>();
  const nowMs = options?.nowMs ?? Date.now;

  function deregisterClient(ws: WSContext): void {
    connectedClients.delete(ws);
    for (const clients of subscribers.values()) {
      clients.delete(ws);
    }
    for (const clients of authorizedQuerySubscribers.values()) {
      clients.delete(ws);
    }
    for (const clients of channelSubscribers.values()) {
      clients.delete(ws);
    }
  }

  function sendEmit(queryKey: string, data: unknown): void {
    const clients = subscribers.get(queryKey);
    if (!clients || clients.size === 0) return;

    const message = JSON.stringify({
      type: "query:updated",
      query: queryKey,
      data,
    });
    for (const ws of clients) {
      try {
        ws.send(message);
      } catch {
        clients.delete(ws);
      }
    }
  }

  function sendInvalidate(queryKeys: string[]): void {
    const targets = new Map<WSContext, Set<string>>();
    for (const queryKey of queryKeys) {
      for (const [subscriptionKey, clients] of subscribers) {
        if (!subscriptionKeyMatchesQueryKey(subscriptionKey, queryKey)) continue;
        for (const ws of clients) {
          if (!targets.has(ws)) targets.set(ws, new Set());
          targets.get(ws)!.add(subscriptionKey);
        }
      }
    }

    for (const [ws, keys] of targets) {
      try {
        ws.send(JSON.stringify({ type: "invalidate", queries: [...keys] }));
      } catch {
        deregisterClient(ws);
      }
    }
  }

  function sendQueryInvalidate(
    query: string,
    subscriptionKey: string,
    scopeHash: string,
    eventId: string,
  ): void {
    if (scopeHash.length === 0) return;
    const clients = authorizedQuerySubscribers.get(subscriptionKey);
    if (!clients || clients.size === 0) return;

    for (const [ws, auth] of [...clients]) {
      if (auth.expiresAt <= nowMs()) {
        clients.delete(ws);
        continue;
      }
      if (auth.scopeHash !== scopeHash) continue;
      try {
        ws.send(
          JSON.stringify({
            type: "query:invalidate",
            query: auth.query || query,
            subscriptionKey,
            eventId,
          }),
        );
      } catch {
        deregisterClient(ws);
      }
    }
  }

  function sendQueryRevoke(
    query: string,
    subscriptionKey: string,
    scopeHash: string,
    epoch?: string,
    reason?: string,
  ): void {
    if (scopeHash.length === 0) return;
    const clients = authorizedQuerySubscribers.get(subscriptionKey);
    if (!clients || clients.size === 0) return;

    for (const [ws, auth] of [...clients]) {
      if (auth.scopeHash !== scopeHash) continue;
      if (epoch !== undefined && auth.epoch === epoch) continue;
      clients.delete(ws);
      try {
        ws.send(
          JSON.stringify({
            type: "query:revoked",
            query: auth.query || query,
            subscriptionKey,
            ...(reason ? { reason } : {}),
          }),
        );
      } catch {
        deregisterClient(ws);
      }
    }
    if (clients.size === 0) authorizedQuerySubscribers.delete(subscriptionKey);
  }

  function sendChannel(channelKey: string, data: unknown, eventId: string): void {
    const clients = channelSubscribers.get(channelKey);
    if (!clients || clients.size === 0) return;

    for (const [ws, auth] of [...clients]) {
      if (auth.expiresAt <= nowMs()) {
        clients.delete(ws);
        continue;
      }
      try {
        ws.send(JSON.stringify({ type: "channel:event", channel: auth.channel, channelKey, eventId, data }));
      } catch {
        deregisterClient(ws);
      }
    }
  }

  function sendChannelInvalidate(channelKey: string): void {
    const clients = channelSubscribers.get(channelKey);
    if (!clients || clients.size === 0) return;

    for (const [ws, auth] of [...clients]) {
      if (auth.expiresAt <= nowMs()) {
        clients.delete(ws);
        continue;
      }
      try {
        ws.send(JSON.stringify({ type: "channel:invalidate", channel: auth.channel, channelKey }));
      } catch {
        deregisterClient(ws);
      }
    }
  }

  function sendChannelRevoke(channelKey: string, epoch?: string, reason?: string): void {
    const clients = channelSubscribers.get(channelKey);
    if (!clients || clients.size === 0) return;

    for (const [ws, auth] of [...clients]) {
      if (epoch !== undefined && auth.epoch === epoch) continue;
      clients.delete(ws);
      try {
        ws.send(
          JSON.stringify({
            type: "channel:revoked",
            channel: auth.channel,
            channelKey,
            ...(reason ? { reason } : {}),
          }),
        );
      } catch {
        deregisterClient(ws);
      }
    }
    if (clients.size === 0) channelSubscribers.delete(channelKey);
  }

  function subscribeChannel(ws: WSContext, token: unknown): void {
    if (!options?.appName || !options?.channelTokenSecret || typeof token !== "string") {
      ws.send(JSON.stringify({ type: "channel:error", error: "invalid_token" }));
      return;
    }
    const verified = verifyRealtimeChannelToken(token, {
      appName: options.appName,
      secret: options.channelTokenSecret,
      nowMs: nowMs(),
    });
    if (!verified.ok) {
      ws.send(JSON.stringify({ type: "channel:error", error: verified.reason }));
      return;
    }
    connectedClients.add(ws);
    let clients = channelSubscribers.get(verified.channelKey);
    if (!clients) {
      clients = new Map();
      channelSubscribers.set(verified.channelKey, clients);
    }
    clients.set(ws, { channel: verified.channel, expiresAt: verified.expiresAt, epoch: verified.epoch });
    ws.send(
      JSON.stringify({
        type: "channel:subscribed",
        channel: verified.channel,
        channelKey: verified.channelKey,
        ...(verified.epoch ? { epoch: verified.epoch } : {}),
      }),
    );
  }

  function subscribeQuery(ws: WSContext, token: unknown): void {
    if (!options?.appName || !options?.channelTokenSecret || typeof token !== "string") {
      ws.send(JSON.stringify({ type: "query:error", error: "invalid_token" }));
      return;
    }
    const verified = verifyRealtimeQuerySubscriptionToken(token, {
      appName: options.appName,
      secret: options.channelTokenSecret,
      nowMs: nowMs(),
    });
    if (!verified.ok) {
      ws.send(JSON.stringify({ type: "query:error", error: verified.reason }));
      return;
    }
    if (verified.policy === "public") {
      ws.send(JSON.stringify({ type: "query:error", error: "invalid_policy" }));
      return;
    }
    connectedClients.add(ws);
    let clients = authorizedQuerySubscribers.get(verified.subscriptionKey);
    if (!clients) {
      clients = new Map();
      authorizedQuerySubscribers.set(verified.subscriptionKey, clients);
    }
    clients.set(ws, {
      query: verified.query,
      subscriptionKey: verified.subscriptionKey,
      scopeHash: verified.scopeHash,
      expiresAt: verified.expiresAt,
      epoch: verified.epoch,
    });
    ws.send(
      JSON.stringify({
        type: "query:subscribed",
        query: verified.query,
        subscriptionKey: verified.subscriptionKey,
        ...(verified.epoch ? { epoch: verified.epoch } : {}),
      }),
    );
  }

  const registry: LocalRealtimeRegistry = {
    notify(event: RealtimeNotifyEvent) {
      if (event.type === "emit") {
        sendEmit(event.queryKey, event.data);
        return;
      }
      if (event.type === "channel_publish") {
        sendChannel(event.channelKey, event.data, event.eventId);
        return;
      }
      if (event.type === "query_invalidate") {
        sendQueryInvalidate(event.query, event.subscriptionKey, event.scopeHash, event.eventId);
        return;
      }
      if (event.type === "query_revoke") {
        sendQueryRevoke(event.query, event.subscriptionKey, event.scopeHash, event.epoch, event.reason);
        return;
      }
      if (event.type === "channel_invalidate") {
        sendChannelInvalidate(event.channelKey);
        return;
      }
      if (event.type === "channel_revoke") {
        sendChannelRevoke(event.channelKey, event.epoch, event.reason);
        return;
      }
      sendInvalidate(event.queryKeys);
    },
    subscribe(queryKey, ws) {
      connectedClients.add(ws);
      if (!subscribers.has(queryKey)) subscribers.set(queryKey, new Set());
      subscribers.get(queryKey)!.add(ws);
    },
    registerClient(ws) {
      connectedClients.add(ws);
    },
    deregisterClient,
    unsubscribeAll: deregisterClient,
    handleWsMessage(ws, raw) {
      try {
        const msg = typeof raw === "string" ? JSON.parse(raw) : JSON.parse(raw.toString());

        if (msg.type === "subscribe" && msg.query) {
          registry.subscribe(msg.query, ws);
          ws.send(JSON.stringify({ type: "subscribed", query: msg.query }));
        }

        if (msg.type === "unsubscribe" && msg.query) {
          subscribers.get(msg.query)?.delete(ws);
        }

        if (msg.type === "subscribeChannel") {
          subscribeChannel(ws, msg.token);
        }

        if (msg.type === "subscribeQuery") {
          subscribeQuery(ws, msg.token);
        }

        if (msg.type === "unsubscribeQuery" && typeof msg.token === "string") {
          const verified =
            options?.appName && options?.channelTokenSecret
              ? verifyRealtimeQuerySubscriptionToken(msg.token, {
                  appName: options.appName,
                  secret: options.channelTokenSecret,
                  nowMs: nowMs(),
                })
              : ({ ok: false } as const);
          if (verified.ok) {
            authorizedQuerySubscribers.get(verified.subscriptionKey)?.delete(ws);
          }
        }

        if (msg.type === "unsubscribeChannel" && typeof msg.token === "string") {
          const verified =
            options?.appName && options?.channelTokenSecret
              ? verifyRealtimeChannelToken(msg.token, {
                  appName: options.appName,
                  secret: options.channelTokenSecret,
                  nowMs: nowMs(),
                })
              : ({ ok: false } as const);
          if (verified.ok) {
            channelSubscribers.get(verified.channelKey)?.delete(ws);
          }
        }
      } catch {
        // ignore malformed messages
      }
    },
  };

  return registry;
}
